import { Badge, Box, Divider, IconButton, Stack, Tooltip } from "@mui/material";
import { grey } from "@mui/material/colors";
import {
  BankNote03Icon,
  MinusCircleIcon,
  PlusCircleIcon,
  Typography,
  Users01Icon,
} from "components";
import useOrgChart from "pages/Company/OrgChart/hook";
import { OrgChart, OrgChartGlobalState } from "services";
import { useGlobalStateStore } from "stores/GlobalState";
import { i18n } from "translations";
import { rem } from "utils";

import Manager from "./Manager";
import CanAccess from "../CanAccess";

interface Props {
  isRootNode?: boolean;
  nodeData: OrgChart;
  hasChildren?: boolean;
  onAction: ReturnType<typeof useOrgChart>["onAction"];
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  isShowEmployees?: boolean;
  onToggleShowEmployees?: () => void;
}

const Node = (props: Props) => {
  const {
    isRootNode = false,
    nodeData,
    hasChildren = false,
    onAction,
    isCollapsed,
    isShowEmployees,
    onToggleCollapse,
  } = props;
  const { costCenterCode, code, deptManagers = [], name, id } = nodeData;
  const orgChartState = useGlobalStateStore<OrgChartGlobalState>({
    name: "orgChart",
  });
  const employeeState = orgChartState?.data?.employeesByDeptId?.[id];

  return (
    <Box
      id={String(id || "")}
      padding="20px 0"
      borderRadius={rem(8)}
      display="inline-block"
      boxShadow="0px 10px 15px -3px rgba(0, 0, 0, 0.1)"
      border={`1px solid ${grey[200]}`}
    >
      <Stack alignItems="center" gap={rem(4)} minWidth="100px">
        {/* Collapse/Expand button */}
        {hasChildren && (
          <IconButton
            aria-label={isCollapsed ? "expand" : "collapse"}
            size="small"
            onClick={onToggleCollapse}
          >
            {isCollapsed ? (
              <PlusCircleIcon fontSize="small" color="success" />
            ) : (
              <MinusCircleIcon fontSize="small" color="error" />
            )}
          </IconButton>
        )}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          width="100%"
        >
          <Stack
            justifyContent="center"
            paddingLeft={rem(10)}
            paddingRight={rem(10)}
            flex={1}
          >
            <Typography
              variant={isRootNode ? "h5" : "body1"}
              sx={{
                color: isRootNode ? "primary.main" : "text.primary",
              }}
            >
              <strong style={{ whiteSpace: "nowrap" }}>
                {name} <br />
                {code && `(${code})`}
              </strong>
              {costCenterCode && (
                <>
                  <br />
                  <Stack
                    direction="row"
                    alignItems="center"
                    columnGap="8px"
                    justifyContent="center"
                  >
                    <BankNote03Icon fontSize="small" />
                    <span style={{ fontSize: "14px" }}>
                      {costCenterCode || ""}
                    </span>
                  </Stack>
                </>
              )}
            </Typography>
            <Stack alignItems="center">
              {deptManagers?.map((item) => (
                <Stack key={item.employee.id}>
                  <Manager item={item} />
                </Stack>
              ))}
            </Stack>
            {!isRootNode && (
              <>
                <Divider sx={{ width: "100%", margin: "8px 0 10px 0" }} />
                <Stack
                  direction="row"
                  spacing={rem(8)}
                  justifyContent="space-around"
                  width="100%"
                >
                  <CanAccess module="CompanyOrgChartShowEmployees">
                    <Badge
                      showZero={employeeState?.fetched}
                      badgeContent={employeeState?.total || 0}
                      color="warning"
                    >
                      <Tooltip
                        title={
                          isShowEmployees
                            ? i18n.t("hideEmployeeList")
                            : i18n.t("showAllEmployees")
                        }
                        arrow
                      >
                        <IconButton
                          aria-label="list-user"
                          size="small"
                          color="secondary"
                          onClick={() => {
                            // eslint-disable-next-line react/destructuring-assignment
                            props.onToggleShowEmployees?.();
                            onAction({ type: "showEmployee", nodeId: id });
                          }}
                        >
                          <Users01Icon />
                          {/* Optionally, you can change icon color or style when active */}
                        </IconButton>
                      </Tooltip>
                    </Badge>
                  </CanAccess>
                </Stack>
              </>
            )}
            {/* Không render danh sách ở đây, NodeUser sẽ được render ở OrgTree */}
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

export default Node;
